import { FormattedMessage } from 'react-intl';
import RouteOutlined from '@mui/icons-material/RouteOutlined';
import { alpha, useTheme } from '@mui/material/styles';
import { Divider, Stack, Typography } from '@mui/material';

function formatMiles(value) {
  if (value == null || !Number.isFinite(Number(value))) {
    return null;
  }
  const n = Number(value);
  return `${n.toLocaleString(undefined, {
    maximumFractionDigits: 1,
    minimumFractionDigits: Number.isInteger(n) ? 0 : 1,
  })} mi`;
}

function formatDriveMinutes(value) {
  if (value == null || !Number.isFinite(Number(value))) {
    return null;
  }
  const m = Math.round(Number(value));
  return `${m} min`;
}

/**
 * Renders straight-line distance, route distance, and estimated drive time.
 *
 * @param {object} props
 * @param {{
 *   straightLineMiles?: number,
 *   routeDistanceMiles?: number,
 *   routeDurationMinutes?: number,
 * }} [props.distanceMetrics]
 */
function DistanceMetrics({ distanceMetrics }) {
  const theme = useTheme();

  if (!distanceMetrics || typeof distanceMetrics !== 'object') {
    return null;
  }

  const { straightLineMiles, routeDistanceMiles, routeDurationMinutes } =
    distanceMetrics;

  const straight = formatMiles(straightLineMiles);
  const route = formatMiles(routeDistanceMiles);
  const duration = formatDriveMinutes(routeDurationMinutes);

  if (!straight && !route && !duration) {
    return null;
  }

  const items = [
    straight && { label: 'Straight line', value: straight },
    route && { label: 'Driving distance', value: route },
    duration && { label: 'Drive time', value: duration },
  ].filter(Boolean);

  return (
    <>
      <Divider
        flexItem
        sx={{ borderColor: alpha(theme.palette.divider, 0.9) }}
      />
      <Stack spacing={1.25}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <RouteOutlined
            sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }}
          />
          <Typography variant="subtitle2" fontWeight={700}>
            <FormattedMessage
              id="DISTANCE_FROM_SEARCH"
              defaultMessage="Distance from search"
            />
          </Typography>
        </Stack>
        <Stack spacing={0.75}>
          {items.map(({ label, value }) => (
            <Stack
              key={label}
              direction="row"
              alignItems="baseline"
              justifyContent="space-between"
              gap={2}
              sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flexShrink: 0 }}>
                {label}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ textAlign: 'right', minWidth: 0 }}>
                {value}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </>
  );
}

export default DistanceMetrics;
